import React, { useState, useEffect, useRef } from 'react';
import { X, Copy, CheckCircle2, Loader2, ShieldCheck, Sparkles, Calendar, Clock, Video, Upload, Mail, Image as ImageIcon, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/workshops`;

// Max screenshot size accepted (2 MB after base64 growth ~ 2.7MB)
const MAX_SCREENSHOT_BYTES = 2 * 1024 * 1024;

const WorkshopEnrollModal = ({ isOpen, onClose, workshopId, onEnrolled }) => {
  const [step, setStep] = useState('form'); // 'form' -> 'payment' -> 'success'
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [copied, setCopied] = useState(false);
  const [proofMode, setProofMode] = useState('utr'); // 'utr' | 'screenshot'
  const fileInputRef = useRef(null);

  const prefillEmail = (typeof window !== 'undefined' && localStorage.getItem('practiq_portal_email')) || '';
  const prefillName = (typeof window !== 'undefined' && localStorage.getItem('practiq_portal_name')) || '';
  const [name, setName] = useState(prefillName);
  const [email, setEmail] = useState(prefillEmail);
  const [phone, setPhone] = useState('');
  const [utr, setUtr] = useState('');
  const [screenshotDataUrl, setScreenshotDataUrl] = useState('');
  const [screenshotFileName, setScreenshotFileName] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen || !workshopId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/detail/${workshopId}`);
        if (!r.ok) throw new Error('workshop not found');
        const data = await r.json();
        if (!cancelled) setDetail(data);
      } catch (_e) {
        toast.error('Could not load workshop details');
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, workshopId]);

  useEffect(() => {
    if (!isOpen) {
      setStep('form');
      setError('');
      setUtr('');
      setScreenshotDataUrl('');
      setScreenshotFileName('');
      setProofMode('utr');
      setCopied(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const qrUrl = detail?.upi_qr_url;
  const upiId = detail?.upi_id;
  const price = detail?.price || 199;

  const proceedToPayment = () => {
    setError('');
    if (!name.trim()) return setError('Please enter your full name');
    if (!/^\S+@\S+\.\S+$/.test(email)) return setError('Please enter a valid email');
    if (!/^\d{10}$/.test(phone.replace(/\D/g, ''))) return setError('Please enter a valid 10-digit phone number');
    setStep('payment');
  };

  const copyUpi = () => {
    if (!upiId) return;
    navigator.clipboard.writeText(upiId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const onFilePicked = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file (JPG / PNG / HEIC).');
      return;
    }
    if (file.size > MAX_SCREENSHOT_BYTES) {
      setError('Screenshot is too large. Please keep it under 2 MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setScreenshotDataUrl(reader.result || '');
      setScreenshotFileName(file.name);
      setError('');
    };
    reader.onerror = () => setError('Could not read the file. Try again.');
    reader.readAsDataURL(file);
  };

  const clearScreenshot = () => {
    setScreenshotDataUrl('');
    setScreenshotFileName('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const submitEnrollment = async () => {
    setError('');
    if (proofMode === 'utr' && utr.trim().length < 4) {
      return setError('Please enter your UPI transaction ID / UTR (usually 10-12 digits).');
    }
    if (proofMode === 'screenshot' && !screenshotDataUrl) {
      return setError('Please upload your payment screenshot.');
    }
    setLoading(true);
    try {
      const body = {
        workshop_id: workshopId,
        name: name.trim(),
        email: email.trim().toLowerCase(),
        phone: phone.trim(),
      };
      if (proofMode === 'utr') body.utr_number = utr.trim();
      if (proofMode === 'screenshot') body.screenshot_data_url = screenshotDataUrl;

      const res = await fetch(`${API}/enroll`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Enrollment failed');
      setStep('success');
      onEnrolled?.();
    } catch (e) {
      setError(e.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="workshop-enroll-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={loading ? undefined : onClose} />
      <div className="relative bg-white border border-slate-200 rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-teal-500 to-cyan-500 px-6 py-4 flex items-center gap-4 flex-shrink-0">
          <div className="w-12 h-12 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-white truncate">{detail?.title || 'Live Workshop'}</h2>
            <p className="text-xs text-white/90 truncate">{detail?.date_label || 'Loading...'}</p>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="ml-auto text-white/80 hover:text-white disabled:opacity-40"
            data-testid="workshop-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto">
          {step === 'form' && (
            <>
              <div className="grid grid-cols-3 gap-3 mb-5">
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-center">
                  <Calendar className="w-4 h-4 text-teal-600 mx-auto mb-1" />
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider">Date</p>
                  <p className="text-xs font-semibold text-slate-900 mt-0.5">23 Aug</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-center">
                  <Clock className="w-4 h-4 text-cyan-600 mx-auto mb-1" />
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider">Time</p>
                  <p className="text-xs font-semibold text-slate-900 mt-0.5">11:00 AM</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-center">
                  <Video className="w-4 h-4 text-emerald-600 mx-auto mb-1" />
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider">Where</p>
                  <p className="text-xs font-semibold text-slate-900 mt-0.5">Zoom</p>
                </div>
              </div>

              <p className="text-sm text-slate-600 mb-5">
                Just <span className="text-slate-900 font-semibold">₹{price}</span> for a 1.5–2 hour hands-on masterclass + Q&amp;A. Certificate included.
              </p>

              <div className="space-y-3">
                <div>
                  <Label className="text-slate-700 mb-1.5 block text-sm font-medium">Full Name *</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Rohan Sharma" className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400" data-testid="workshop-name-input" />
                </div>
                <div>
                  <Label className="text-slate-700 mb-1.5 block text-sm font-medium">Email *</Label>
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400" data-testid="workshop-email-input" />
                  <p className="text-[11px] text-slate-500 mt-1">We&apos;ll send your Zoom link here.</p>
                </div>
                <div>
                  <Label className="text-slate-700 mb-1.5 block text-sm font-medium">Phone (WhatsApp) *</Label>
                  <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="10-digit mobile" className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400" data-testid="workshop-phone-input" />
                </div>
              </div>

              {error && (
                <div className="mt-4 p-3 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-700">
                  {error}
                </div>
              )}

              <Button
                onClick={proceedToPayment}
                className="w-full mt-5 h-12 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-500 hover:to-cyan-500 text-white font-semibold shadow-md shadow-teal-200"
                data-testid="workshop-proceed-btn"
              >
                Continue to Payment — ₹{price}
              </Button>
            </>
          )}

          {step === 'payment' && (
            <>
              <div className="text-center mb-4">
                <h3 className="text-slate-900 font-semibold mb-1">Scan &amp; Pay ₹{price}</h3>
                <p className="text-xs text-slate-500">Any UPI app · GPay · PhonePe · Paytm · BHIM</p>
              </div>

              <div className="bg-white border-2 border-slate-200 rounded-xl p-4 flex justify-center mb-4">
                {qrUrl ? (
                  <img
                    src={qrUrl}
                    alt="UPI QR Code"
                    className="w-52 h-52 object-contain"
                    onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200"><rect fill="%23f0f0f0" width="200" height="200"/><text x="50%" y="50%" text-anchor="middle" fill="%23666" font-size="14">Loading QR...</text></svg>'; }}
                  />
                ) : (
                  <div className="w-52 h-52 flex items-center justify-center">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 mb-4">
                <span className="text-xs text-slate-500">UPI ID:</span>
                <span className="text-sm font-mono text-slate-900 flex-1 truncate">{upiId || '—'}</span>
                <button onClick={copyUpi} className="text-teal-600 hover:text-teal-700" title="Copy UPI ID" data-testid="workshop-copy-upi">
                  {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>

              <p className="text-sm text-slate-900 font-medium mb-2">Confirm your payment</p>
              <p className="text-xs text-slate-500 mb-3">Share your UTR number <em>or</em> upload the payment screenshot — either works.</p>

              <div className="grid grid-cols-2 gap-2 mb-4 p-1 bg-slate-100 border border-slate-200 rounded-lg">
                <button
                  onClick={() => setProofMode('utr')}
                  className={`text-sm font-medium py-2 rounded-md transition-all ${
                    proofMode === 'utr'
                      ? 'bg-gradient-to-r from-teal-600 to-cyan-600 text-white shadow'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                  data-testid="proof-mode-utr"
                >
                  Enter UTR
                </button>
                <button
                  onClick={() => setProofMode('screenshot')}
                  className={`text-sm font-medium py-2 rounded-md transition-all ${
                    proofMode === 'screenshot'
                      ? 'bg-gradient-to-r from-teal-600 to-cyan-600 text-white shadow'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                  data-testid="proof-mode-screenshot"
                >
                  Upload Screenshot
                </button>
              </div>

              {proofMode === 'utr' ? (
                <div>
                  <Label className="text-slate-700 mb-1.5 block text-sm font-medium">UPI Transaction ID / UTR *</Label>
                  <Input
                    value={utr}
                    onChange={(e) => setUtr(e.target.value)}
                    placeholder="e.g. 512345678901"
                    className="bg-white border-slate-300 text-slate-900 font-mono placeholder:text-slate-400"
                    data-testid="workshop-utr-input"
                  />
                  <p className="text-[11px] text-slate-500 mt-1.5">Find this in your UPI app under the payment&apos;s details.</p>
                </div>
              ) : (
                <div>
                  <Label className="text-slate-700 mb-1.5 block text-sm font-medium">Payment Screenshot *</Label>
                  {!screenshotDataUrl ? (
                    <label className="flex flex-col items-center justify-center border-2 border-dashed border-slate-300 hover:border-teal-500 rounded-lg p-6 cursor-pointer transition-colors bg-slate-50">
                      <Upload className="w-6 h-6 text-teal-600 mb-2" />
                      <p className="text-sm text-slate-900 font-medium">Click to upload screenshot</p>
                      <p className="text-[11px] text-slate-500 mt-1">JPG / PNG — max 2 MB</p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={onFilePicked}
                        data-testid="workshop-screenshot-input"
                      />
                    </label>
                  ) : (
                    <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
                      <div className="flex items-center gap-3">
                        <img src={screenshotDataUrl} alt="Payment proof" className="w-16 h-16 rounded object-cover flex-shrink-0 border border-slate-200" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <ImageIcon className="w-3.5 h-3.5 text-teal-600 flex-shrink-0" />
                            <p className="text-sm text-slate-900 truncate">{screenshotFileName || 'Screenshot'}</p>
                          </div>
                          <p className="text-[11px] text-emerald-600 font-medium">Ready to submit</p>
                        </div>
                        <button
                          onClick={clearScreenshot}
                          className="text-rose-600 hover:text-rose-700 p-1"
                          title="Remove"
                          data-testid="workshop-screenshot-clear"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                  <p className="text-[11px] text-slate-500 mt-1.5">The screenshot from your UPI app showing the successful ₹{price} payment.</p>
                </div>
              )}

              <div className="mt-4 bg-teal-50 border border-teal-200 rounded-lg p-3 flex items-start gap-2">
                <Mail className="w-4 h-4 text-teal-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-teal-800 leading-relaxed">
                  Once submitted, our team will verify your payment and send the <strong>Zoom join link</strong> to your email — usually within a few hours.
                </p>
              </div>

              {error && (
                <div className="mt-3 p-3 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-700">
                  {error}
                </div>
              )}

              <div className="flex gap-2 mt-5">
                <Button
                  variant="outline"
                  onClick={() => setStep('form')}
                  disabled={loading}
                  className="flex-1 border-slate-300 text-slate-700 bg-white hover:bg-slate-50"
                >
                  Back
                </Button>
                <Button
                  onClick={submitEnrollment}
                  disabled={loading}
                  className="flex-[2] h-11 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-500 hover:to-cyan-500 text-white font-semibold shadow-md shadow-teal-200"
                  data-testid="workshop-submit-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Enrollment'}
                </Button>
              </div>
            </>
          )}

          {step === 'success' && (
            <div className="text-center py-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="w-9 h-9 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Congratulations, you&apos;re in! 🎉</h3>
              <p className="text-sm text-slate-600 mb-5 max-w-sm mx-auto">
                A confirmation has been sent to <span className="text-teal-600 font-medium">{email}</span>. Our team will verify your payment and email you the <strong className="text-slate-900">Zoom join link</strong> shortly.
              </p>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-left space-y-2 mb-5">
                <div className="flex items-start gap-2 text-sm">
                  <Mail className="w-4 h-4 text-teal-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Watch your inbox — confirmation email is on its way.</span>
                </div>
                <div className="flex items-start gap-2 text-sm">
                  <ShieldCheck className="w-4 h-4 text-cyan-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Track your enrollment under <strong className="text-slate-900">My Workshops</strong> in your dashboard.</span>
                </div>
                <div className="flex items-start gap-2 text-sm">
                  <Sparkles className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Certificate of Participation issued after the session.</span>
                </div>
              </div>
              <Button
                onClick={onClose}
                className="w-full h-11 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-500 hover:to-cyan-500 text-white shadow-md shadow-teal-200"
                data-testid="workshop-success-close"
              >
                Got it
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkshopEnrollModal;
